import {
  type ProfileModuleDto,
  ProfileTemplateFieldMode,
} from '@up2stay/up2promo-api-client';
import {
  ensureProfileModuleEntryIds,
  getModuleId,
  getModuleListConfigKey,
  isEntryLocked,
  mergeListModuleConfig,
  mergeModuleConfig,
  MODULE_ENTRY_WHOLE_MODULE_KEY,
  resolveEntryPolicy,
  stripTemplatePolicyFromModule,
  stripTemplatePolicyFromModules,
} from '@up2stay/up2promo-commons';

import type {
  TemplateAssetPreview,
  TemplateAssetPreviewLayout,
} from '@/core/components/profile-templates/template-customization-hint';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import type { TemplateFormFieldDisplay } from '@/core/lib/profile-template-overrides';

export {
  ensureProfileModuleEntryIds,
  getModuleId,
  MODULE_ENTRY_WHOLE_MODULE_KEY,
  resolveEntryPolicy,
  stripTemplatePolicyFromModule,
  stripTemplatePolicyFromModules,
};

type EntryWithPolicy = { id?: string; policy?: string };

export function isEntryPolicyLocked(
  templateModule: ProfileModuleDto,
  entryId: string,
): boolean {
  return isEntryLocked(templateModule, entryId);
}

export type ModuleEntryDescriptor = {
  entryId: string;
  label: string;
};

export function listModuleEntries(
  module: ProfileModuleDto,
): ModuleEntryDescriptor[] {
  const config = (module.config ?? {}) as Record<string, unknown>;
  const moduleId = getModuleId(module);

  switch (module.type) {
    case 'socials': {
      const links =
        (config.links as Array<{
          id?: string;
          platform?: string;
          label?: string;
        }>) ?? [];
      return links.map((link, i) => ({
        entryId: link.id ?? `${i}`,
        label: link.label?.trim() || link.platform || `Social ${i + 1}`,
      }));
    }
    case 'contact_info': {
      const items =
        (config.items as Array<{
          id?: string;
          kind?: string;
          value?: string;
          label?: string;
        }>) ?? [];
      return items.map((item, i) => ({
        entryId: item.id ?? `${i}`,
        label:
          item.label?.trim() ||
          item.value?.trim() ||
          `${item.kind === 'phone' ? 'Phone' : 'Email'} ${i + 1}`,
      }));
    }
    case 'links': {
      const items =
        (config.items as Array<{ id?: string; title?: string }>) ?? [];
      return items.map((item, i) => ({
        entryId: item.id ?? `${i}`,
        label: item.title?.trim() || `Link ${i + 1}`,
      }));
    }
    case 'images':
      return [
        {
          entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
          label: module.type,
        },
      ];
    case 'files':
    case 'team_members':
      return [
        {
          entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
          label: module.type,
        },
      ];
    case 'biography':
    case 'video':
    case 'google_maps':
      return [
        {
          entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
          label: module.type,
        },
      ];
    default:
      return moduleId
        ? [{ entryId: MODULE_ENTRY_WHOLE_MODULE_KEY, label: module.type }]
        : [];
  }
}

export function templateGovernanceActive(
  effectiveTemplateId: string | null | undefined,
): boolean {
  return Boolean(effectiveTemplateId);
}

export function defaultPolicyForNewEntry(): ProfileTemplateFieldMode {
  return ProfileTemplateFieldMode.FREE;
}

function setPolicyOnEntry<T extends EntryWithPolicy>(
  entry: T,
  mode: ProfileTemplateFieldMode,
): T {
  return { ...entry, policy: mode };
}

/** Updates embedded `policy` on a template module entry (template editor). */
export function setEntryPolicyOnModule(
  modules: ProfileModuleDto[],
  moduleId: string,
  entryId: string,
  mode: ProfileTemplateFieldMode,
): ProfileModuleDto[] {
  return modules.map((module) => {
    if (getModuleId(module) !== moduleId) return module;
    const config = { ...(module.config ?? {}) } as Record<string, unknown>;

    if (entryId === MODULE_ENTRY_WHOLE_MODULE_KEY) {
      return { ...module, config: { ...config, policy: mode } };
    }

    const updateList = <T extends EntryWithPolicy>(
      items: T[] | undefined,
    ): T[] =>
      (items ?? []).map((item) =>
        item.id === entryId ? setPolicyOnEntry(item, mode) : item,
      );

    switch (module.type) {
      case 'socials':
        config.links = updateList(
          config.links as EntryWithPolicy[] | undefined,
        );
        break;
      case 'contact_info':
        config.items = updateList(
          config.items as EntryWithPolicy[] | undefined,
        );
        break;
      case 'links':
        config.items = updateList(
          config.items as EntryWithPolicy[] | undefined,
        );
        break;
      default:
        break;
    }

    return { ...module, config };
  });
}

const ASSETS_LIST_MODULE_TYPES = new Set(['images', 'files', 'team_members']);

export function isAssetsListModuleType(moduleType: string): boolean {
  return ASSETS_LIST_MODULE_TYPES.has(moduleType);
}

function getAssetsListConfigKey(
  moduleType: string,
): 'images' | 'files' | 'members' | null {
  switch (moduleType) {
    case 'images':
      return 'images';
    case 'files':
      return 'files';
    case 'team_members':
      return 'members';
    default:
      return null;
  }
}

function getAssetsEntries(
  config: Record<string, unknown>,
  moduleType: string,
): Array<Record<string, unknown> & { id?: string }> {
  const listKey = getAssetsListConfigKey(moduleType);
  if (!listKey) return [];
  return (
    (config[listKey] as Array<Record<string, unknown> & { id?: string }>) ?? []
  );
}

function stripAssetEntryForCompare(
  entry: Record<string, unknown>,
  moduleType: string,
): Record<string, unknown> {
  switch (moduleType) {
    case 'images':
    case 'files': {
      const file = entry.file as Record<string, unknown> | undefined;
      return {
        title: entry.title,
        description: entry.description,
        category: entry.category,
        order: entry.order,
        visible: entry.visible,
        fileRef: file?.id ?? file?.path ?? null,
      };
    }
    case 'team_members': {
      const avatar = entry.avatar as Record<string, unknown> | undefined;
      return {
        name: entry.name,
        role: entry.role,
        title: entry.title,
        bio: entry.bio,
        email: entry.email,
        phone: entry.phone,
        profileUrl: entry.profileUrl,
        userId: entry.userId,
        username: entry.username,
        order: entry.order,
        visible: entry.visible,
        avatarRef: avatar?.id ?? avatar?.path ?? null,
      };
    }
    default:
      return stripEntryPolicyForCompare(entry);
  }
}

const ASSETS_MODULE_SETTING_KEYS: Record<string, string[]> = {
  images: ['aspectRatio'],
  files: ['showDescriptions', 'showFileSize'],
  team_members: [
    'layout',
    'columns',
    'showRoles',
    'showBios',
    'showContactInfo',
    'showProfileLinks',
  ],
};

function areAssetsModuleSettingsEqual(
  currentConfig: Record<string, unknown>,
  templateConfig: Record<string, unknown>,
  moduleType: string,
): boolean {
  const keys = ASSETS_MODULE_SETTING_KEYS[moduleType] ?? [];
  return keys.every((key) =>
    entryFieldValuesEqual(currentConfig[key], templateConfig[key]),
  );
}

const IMAGE_FILE_PATH_PATTERN = /\.(jpe?g|png|gif|webp)$/i;

function getEntryFilePath(entry: Record<string, unknown>): string | undefined {
  const file = entry.file as { path?: string } | undefined;
  const path = file?.path;
  return typeof path === 'string' && path.trim() ? path.trim() : undefined;
}

function isImageFilePath(path: string): boolean {
  return IMAGE_FILE_PATH_PATTERN.test(path);
}

function getImageEntryPreviewLabel(
  entry: Record<string, unknown>,
  index: number,
): string {
  const title = (entry.title as string)?.trim();
  if (title) {
    return title;
  }
  const path = getEntryFilePath(entry);
  if (path) {
    const filename = path.split('/').pop() ?? '';
    const withoutExt = filename.replace(/\.[^.]+$/, '');
    if (withoutExt) {
      return withoutExt;
    }
    if (filename) {
      return filename;
    }
  }
  return `Bild ${index + 1}`;
}

function getFileEntryDisplayTitle(
  entry: Record<string, unknown>,
  _index: number,
): string | undefined {
  const title = (entry.title as string)?.trim();
  return title || undefined;
}

function buildAssetEntryPreview(
  entry: Record<string, unknown>,
  index: number,
  moduleType: 'images' | 'files',
): TemplateAssetPreview | null {
  const path = getEntryFilePath(entry);
  const title = getFileEntryDisplayTitle(entry, index);

  if (moduleType === 'images') {
    if (!path) {
      return null;
    }
    return {
      kind: 'image',
      src: getAbsoluteFileUrl(path),
      title,
    };
  }

  if (!path && !title) {
    return null;
  }

  if (path && isImageFilePath(path)) {
    return {
      kind: 'image',
      src: getAbsoluteFileUrl(path),
      title,
    };
  }

  return {
    kind: 'file',
    title,
  };
}

function assetPreviewLayoutForModule(
  moduleType: string,
): TemplateAssetPreviewLayout | undefined {
  return moduleType === 'files' ? 'list' : undefined;
}

function getAssetEntryLabel(
  entry: Record<string, unknown>,
  moduleType: string,
  index: number,
): string {
  switch (moduleType) {
    case 'images':
      return getImageEntryPreviewLabel(entry, index);
    case 'files': {
      const title = (entry.title as string)?.trim();
      if (title) {
        return title;
      }
      const file = entry.file as { path?: string } | undefined;
      const path = file?.path;
      if (path) {
        return path.split('/').pop() ?? `Datei ${index + 1}`;
      }
      return `Datei ${index + 1}`;
    }
    case 'team_members':
      return (entry.name as string)?.trim() || `Mitglied ${index + 1}`;
    default:
      return `Eintrag ${index + 1}`;
  }
}

function getOrderedEntryIds(
  entries: Array<Record<string, unknown> & { id?: string }>,
): string[] {
  return entries
    .map((entry) => entry.id)
    .filter((id): id is string => Boolean(id));
}

function areAssetsListsEqual(
  currentEntries: Array<Record<string, unknown> & { id?: string }>,
  templateEntries: Array<Record<string, unknown> & { id?: string }>,
  moduleType: string,
): boolean {
  if (currentEntries.length !== templateEntries.length) {
    return false;
  }

  const templateIds = getOrderedEntryIds(templateEntries);
  const currentIds = getOrderedEntryIds(currentEntries);
  const canCompareById =
    templateIds.length === templateEntries.length &&
    currentIds.length === currentEntries.length;

  if (canCompareById) {
    if (templateIds.join('\0') !== currentIds.join('\0')) {
      return false;
    }
    const currentById = new Map(
      currentEntries
        .filter((entry) => entry.id)
        .map((entry) => [entry.id!, entry]),
    );
    return templateEntries.every((templateEntry) => {
      const entryId = templateEntry.id ?? '';
      const currentEntry = currentById.get(entryId);
      if (!currentEntry) {
        return false;
      }
      return (
        JSON.stringify(stripAssetEntryForCompare(currentEntry, moduleType)) ===
        JSON.stringify(stripAssetEntryForCompare(templateEntry, moduleType))
      );
    });
  }

  return templateEntries.every((templateEntry, index) => {
    const currentEntry = currentEntries[index];
    if (!currentEntry) {
      return false;
    }
    return (
      JSON.stringify(stripAssetEntryForCompare(currentEntry, moduleType)) ===
      JSON.stringify(stripAssetEntryForCompare(templateEntry, moduleType))
    );
  });
}

function isAssetsModuleContentCustomized(
  currentModule: ProfileModuleDto,
  templateModule: ProfileModuleDto,
): boolean {
  const moduleType = templateModule.type;
  const templateConfig = (templateModule.config ?? {}) as Record<
    string,
    unknown
  >;
  const currentConfig = (currentModule.config ?? {}) as Record<string, unknown>;

  if (
    !areAssetsModuleSettingsEqual(currentConfig, templateConfig, moduleType)
  ) {
    return true;
  }

  if (moduleType === 'team_members') {
    return false;
  }

  return !areAssetsListsEqual(
    getAssetsEntries(currentConfig, moduleType),
    getAssetsEntries(templateConfig, moduleType),
    moduleType,
  );
}

function findMatchingProfileModule(
  templateModule: ProfileModuleDto,
  profileModules: ProfileModuleDto[],
  templateIndex: number,
): ProfileModuleDto | undefined {
  const templateModuleId = getModuleId(templateModule);

  if (templateModuleId) {
    const byId = profileModules.find(
      (module) => getModuleId(module) === templateModuleId,
    );
    if (byId) return byId;
  }

  const sameType = profileModules.filter(
    (module) => module.type === templateModule.type,
  );
  if (sameType.length === 0) return undefined;

  const byOrder = sameType.find(
    (module) =>
      module.order != null &&
      templateModule.order != null &&
      module.order === templateModule.order,
  );
  if (byOrder) return byOrder;

  const sameTypeIndex = profileModules
    .slice(0, templateIndex + 1)
    .filter((module) => module.type === templateModule.type).length;
  return sameType[sameTypeIndex - 1] ?? sameType[0];
}

/** Applies template module structure with profile overrides for free entries. */
export function mergeModulesForProfileEdit(
  profileModules: ProfileModuleDto[],
  templateModules: ProfileModuleDto[],
): ProfileModuleDto[] {
  const template = ensureProfileModuleEntryIds(templateModules);
  const profile = ensureProfileModuleEntryIds(profileModules);

  return template
    .map((templateModule, index) => {
      const profileModule = findMatchingProfileModule(
        templateModule,
        profile,
        index,
      );
      if (!profileModule) {
        return templateModule;
      }

      const config = mergeModuleConfig(templateModule, profileModule, {
        resyncOnly: false,
      });

      return {
        ...templateModule,
        enabled: profileModule.enabled ?? templateModule.enabled,
        config,
      };
    })
    .map((module) => stripTemplatePolicyFromModule(module));
}

function stripEntryPolicyForCompare(
  entry: Record<string, unknown>,
): Record<string, unknown> {
  const { policy: _policy, ...rest } = entry;
  return rest;
}

function stripModuleConfigPolicyForCompare(
  config: Record<string, unknown>,
): Record<string, unknown> {
  const { policy: _policy, ...rest } = config;
  return rest;
}

function listEntryHasMeaningfulContent(
  entry: Record<string, unknown>,
  moduleType: string,
): boolean {
  switch (moduleType) {
    case 'socials':
      return Boolean((entry.url as string)?.trim());
    case 'contact_info':
      return Boolean((entry.value as string)?.trim());
    case 'links':
      return Boolean(
        (entry.title as string)?.trim() || (entry.url as string)?.trim(),
      );
    default:
      return true;
  }
}

/** True when the template module has user-facing content (not just structure). */
const PER_ENTRY_LIST_MODULE_TYPES = new Set([
  'links',
  'socials',
  'contact_info',
]);

export function isPerEntryListModuleType(moduleType: string): boolean {
  return PER_ENTRY_LIST_MODULE_TYPES.has(moduleType);
}

function findListEntryById(
  module: ProfileModuleDto,
  entryId: string,
): Record<string, unknown> | undefined {
  const listKey = getModuleListConfigKey(module.type);
  if (!listKey) return undefined;
  const config = (module.config ?? {}) as Record<string, unknown>;
  const entries =
    (config[listKey] as Array<Record<string, unknown> & { id?: string }>) ?? [];
  return entries.find((entry) => entry.id === entryId);
}

/** True when a free list entry differs from its template counterpart. */
export function isModuleEntryContentCustomized(
  currentModule: ProfileModuleDto,
  templateModule: ProfileModuleDto,
  entryId: string,
): boolean {
  if (!isPerEntryListModuleType(templateModule.type)) {
    return false;
  }
  if (isEntryPolicyLocked(templateModule, entryId)) {
    return false;
  }
  const templateEntry = findListEntryById(templateModule, entryId);
  if (
    !templateEntry ||
    !listEntryHasMeaningfulContent(templateEntry, templateModule.type)
  ) {
    return false;
  }
  const currentEntry = findListEntryById(currentModule, entryId);
  if (!currentEntry) {
    return false;
  }
  return (
    JSON.stringify(stripEntryPolicyForCompare(currentEntry)) !==
    JSON.stringify(stripEntryPolicyForCompare(templateEntry))
  );
}

export type TemplateEntryFieldLabels = {
  emptyValue: string;
  contactLabel: string;
  contactValue: string;
  contactKind: string;
  linkTitle: string;
  linkUrl: string;
  linkDescription: string;
  socialLabel: string;
  socialUrl: string;
  socialPlatform: string;
  biographyContent: string;
  videoTitle: string;
  videoUrl: string;
  mapsAddress: string;
  imageTitle: string;
  imageDescription: string;
  imageFile: string;
  imageAspectRatio: string;
  fileTitle: string;
  fileDescription: string;
  fileCategory: string;
  fileAsset: string;
  memberName: string;
  memberRole: string;
  memberTitle: string;
  memberAvatar: string;
  entryOrder: string;
};

function displayEntryFieldValue(value: unknown, emptyValue: string): string {
  if (value === null || value === undefined) {
    return emptyValue;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  const trimmed = String(value).trim();
  return trimmed || emptyValue;
}

function entryFieldValuesEqual(a: unknown, b: unknown): boolean {
  const normalize = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value).trim();
  };
  return normalize(a) === normalize(b);
}

function getListEntryFieldDiffs(
  currentEntry: Record<string, unknown>,
  templateEntry: Record<string, unknown>,
  moduleType: string,
  labels: TemplateEntryFieldLabels,
): NonNullable<TemplateFormFieldDisplay['details']> {
  const details: NonNullable<TemplateFormFieldDisplay['details']> = [];

  const pushIfDifferent = (
    fieldKey: string,
    fieldLabel: string,
    templateValue: unknown,
  ) => {
    if (
      !entryFieldValuesEqual(currentEntry[fieldKey], templateEntry[fieldKey])
    ) {
      details.push({
        label: fieldLabel,
        value: displayEntryFieldValue(templateValue, labels.emptyValue),
      });
    }
  };

  switch (moduleType) {
    case 'contact_info':
      pushIfDifferent('label', labels.contactLabel, templateEntry.label);
      pushIfDifferent('value', labels.contactValue, templateEntry.value);
      pushIfDifferent('kind', labels.contactKind, templateEntry.kind);
      break;
    case 'links':
      pushIfDifferent('title', labels.linkTitle, templateEntry.title);
      pushIfDifferent('url', labels.linkUrl, templateEntry.url);
      pushIfDifferent(
        'description',
        labels.linkDescription,
        templateEntry.description,
      );
      break;
    case 'socials':
      pushIfDifferent('label', labels.socialLabel, templateEntry.label);
      pushIfDifferent('url', labels.socialUrl, templateEntry.url);
      pushIfDifferent(
        'platform',
        labels.socialPlatform,
        templateEntry.platform,
      );
      break;
    default:
      break;
  }

  return details;
}

/** Template values for only the entry fields that differ from the template. */
export function getTemplateEntryOverrideDisplay(
  currentModule: ProfileModuleDto,
  templateModule: ProfileModuleDto,
  entryId: string,
  labels: TemplateEntryFieldLabels,
): TemplateFormFieldDisplay | null {
  if (!isModuleEntryContentCustomized(currentModule, templateModule, entryId)) {
    return null;
  }

  const currentEntry = findListEntryById(currentModule, entryId);
  const templateEntry = findListEntryById(templateModule, entryId);
  if (!currentEntry || !templateEntry) {
    return null;
  }

  const details = getListEntryFieldDiffs(
    currentEntry,
    templateEntry,
    templateModule.type,
    labels,
  );
  if (details.length === 0) {
    return null;
  }
  if (details.length === 1) {
    const [first] = details;
    return { single: `${first!.label}: ${first!.value}` };
  }
  return { details };
}

export function templateModuleHasMeaningfulContent(
  templateModule: ProfileModuleDto,
): boolean {
  const config = (templateModule.config ?? {}) as Record<string, unknown>;
  const type = templateModule.type;
  const listKey = getModuleListConfigKey(type);

  if (listKey) {
    const entries =
      (config[listKey] as Array<Record<string, unknown>> | undefined) ?? [];
    return entries.some((entry) => listEntryHasMeaningfulContent(entry, type));
  }

  switch (type) {
    case 'biography':
      return Boolean((config.content as string)?.trim());
    case 'video':
      return Boolean((config.url as string)?.trim());
    case 'google_maps':
      return Boolean(
        (config.locationId as string | undefined)?.trim() ||
        (config.address as string)?.trim(),
      );
    case 'images':
      return ((config.images as unknown[] | undefined)?.length ?? 0) > 0;
    case 'files':
      return ((config.files as unknown[] | undefined)?.length ?? 0) > 0;
    case 'team_members':
      return true;
    default:
      return false;
  }
}

/** True when editable (free) module content differs from the template baseline. */
export function isModuleContentCustomized(
  currentModule: ProfileModuleDto,
  templateModule: ProfileModuleDto,
): boolean {
  if (!templateModuleHasMeaningfulContent(templateModule)) {
    return false;
  }

  const listKey = getModuleListConfigKey(templateModule.type);

  if (listKey && isPerEntryListModuleType(templateModule.type)) {
    const templateEntries =
      (((templateModule.config ?? {}) as Record<string, unknown>)[listKey] as
        | Array<Record<string, unknown> & { id?: string }>
        | undefined) ?? [];
    return templateEntries.some((templateEntry) => {
      const entryId = templateEntry.id ?? '';
      if (!entryId) return false;
      return isModuleEntryContentCustomized(
        currentModule,
        templateModule,
        entryId,
      );
    });
  }
  const templateConfig = (templateModule.config ?? {}) as Record<
    string,
    unknown
  >;
  const currentConfig = (currentModule.config ?? {}) as Record<string, unknown>;

  if (isAssetsListModuleType(templateModule.type)) {
    if (isEntryPolicyLocked(templateModule, MODULE_ENTRY_WHOLE_MODULE_KEY)) {
      return false;
    }
    return isAssetsModuleContentCustomized(currentModule, templateModule);
  }

  if (!listKey) {
    if (isEntryPolicyLocked(templateModule, MODULE_ENTRY_WHOLE_MODULE_KEY)) {
      return false;
    }
    return (
      JSON.stringify(stripModuleConfigPolicyForCompare(currentConfig)) !==
      JSON.stringify(stripModuleConfigPolicyForCompare(templateConfig))
    );
  }

  const templateEntries =
    (templateConfig[listKey] as Array<
      Record<string, unknown> & { id?: string }
    >) ?? [];
  const currentEntries =
    (currentConfig[listKey] as Array<
      Record<string, unknown> & { id?: string }
    >) ?? [];
  const templateIds = new Set(
    templateEntries.map((entry) => entry.id).filter(Boolean),
  );

  for (const entry of currentEntries) {
    if (entry.id && !templateIds.has(entry.id)) {
      return true;
    }
  }

  for (const templateEntry of templateEntries) {
    const entryId = templateEntry.id ?? '';
    if (
      !entryId ||
      isEntryPolicyLocked(templateModule, entryId) ||
      !listEntryHasMeaningfulContent(templateEntry, templateModule.type)
    ) {
      continue;
    }
    const currentEntry = currentEntries.find((entry) => entry.id === entryId);
    if (!currentEntry) {
      continue;
    }
    if (
      JSON.stringify(stripEntryPolicyForCompare(currentEntry)) !==
      JSON.stringify(stripEntryPolicyForCompare(templateEntry))
    ) {
      return true;
    }
  }

  return false;
}

/** Resets free module content to match the template (keeps enabled state). */
export function resetModuleContentToTemplate(
  modules: ProfileModuleDto[],
  templateModules: ProfileModuleDto[],
  moduleId: string,
): ProfileModuleDto[] {
  const templateModule = templateModules.find(
    (module) => getModuleId(module) === moduleId,
  );
  if (!templateModule) {
    return modules;
  }

  return modules.map((currentModule) => {
    if (getModuleId(currentModule) !== moduleId) {
      return currentModule;
    }

    const listKey = getModuleListConfigKey(templateModule.type);
    const templateConfig = (templateModule.config ?? {}) as Record<
      string,
      unknown
    >;

    const config = listKey
      ? mergeListModuleConfig(
          templateModule,
          { type: templateModule.type, config: {} },
          listKey,
          { resyncOnly: false },
        )
      : { ...templateConfig };

    return {
      ...currentModule,
      config,
    };
  });
}

export type TemplateModuleDisplayLabels = TemplateEntryFieldLabels & {
  moduleNoContent: string;
  moduleEntries: string;
};

function getWholeModuleFieldDiffs(
  currentConfig: Record<string, unknown>,
  templateConfig: Record<string, unknown>,
  moduleType: string,
  labels: TemplateModuleDisplayLabels,
): NonNullable<TemplateFormFieldDisplay['details']> {
  const details: NonNullable<TemplateFormFieldDisplay['details']> = [];

  const pushIfDifferent = (
    fieldKey: string,
    fieldLabel: string,
    templateValue: unknown,
  ) => {
    if (
      !entryFieldValuesEqual(currentConfig[fieldKey], templateConfig[fieldKey])
    ) {
      details.push({
        label: fieldLabel,
        value: displayEntryFieldValue(templateValue, labels.emptyValue),
      });
    }
  };

  switch (moduleType) {
    case 'biography':
      pushIfDifferent(
        'content',
        labels.biographyContent,
        templateConfig.content,
      );
      break;
    case 'video':
      pushIfDifferent('title', labels.videoTitle, templateConfig.title);
      pushIfDifferent('url', labels.videoUrl, templateConfig.url);
      break;
    case 'google_maps':
      pushIfDifferent(
        'locationId',
        labels.mapsAddress,
        templateConfig.locationId ?? templateConfig.address,
      );
      break;
    default:
      break;
  }

  return details;
}

function getAssetsModuleSettingDiffs(
  currentConfig: Record<string, unknown>,
  templateConfig: Record<string, unknown>,
  moduleType: string,
  labels: TemplateModuleDisplayLabels,
): NonNullable<TemplateFormFieldDisplay['details']> {
  const details: NonNullable<TemplateFormFieldDisplay['details']> = [];
  const keys = ASSETS_MODULE_SETTING_KEYS[moduleType] ?? [];

  const labelForKey = (key: string): string => {
    if (key === 'aspectRatio') return labels.imageAspectRatio;
    return key;
  };

  for (const key of keys) {
    if (!entryFieldValuesEqual(currentConfig[key], templateConfig[key])) {
      details.push({
        label: labelForKey(key),
        value: displayEntryFieldValue(templateConfig[key], labels.emptyValue),
      });
    }
  }

  return details;
}

function getAssetsEntryFieldDiffs(
  currentEntry: Record<string, unknown>,
  templateEntry: Record<string, unknown>,
  moduleType: string,
  labels: TemplateModuleDisplayLabels,
): NonNullable<TemplateFormFieldDisplay['details']> {
  const details: NonNullable<TemplateFormFieldDisplay['details']> = [];

  const pushIfDifferent = (
    fieldKey: string,
    fieldLabel: string,
    templateValue: unknown,
  ) => {
    if (
      !entryFieldValuesEqual(currentEntry[fieldKey], templateEntry[fieldKey])
    ) {
      details.push({
        label: fieldLabel,
        value: displayEntryFieldValue(templateValue, labels.emptyValue),
      });
    }
  };

  switch (moduleType) {
    case 'images':
      pushIfDifferent('title', labels.imageTitle, templateEntry.title);
      pushIfDifferent(
        'description',
        labels.imageDescription,
        templateEntry.description,
      );
      break;
    case 'files':
      pushIfDifferent('title', labels.fileTitle, templateEntry.title);
      pushIfDifferent(
        'description',
        labels.fileDescription,
        templateEntry.description,
      );
      pushIfDifferent('category', labels.fileCategory, templateEntry.category);
      break;
    case 'team_members':
      pushIfDifferent('name', labels.memberName, templateEntry.name);
      pushIfDifferent('role', labels.memberRole, templateEntry.role);
      pushIfDifferent('title', labels.memberTitle, templateEntry.title);
      break;
    default:
      break;
  }

  const templateFile = templateEntry.file as
    | Record<string, unknown>
    | undefined;
  const currentFile = currentEntry.file as Record<string, unknown> | undefined;
  if (moduleType === 'images' || moduleType === 'files') {
    const templateRef = templateFile?.id ?? templateFile?.path;
    const currentRef = currentFile?.id ?? currentFile?.path;
    if (!entryFieldValuesEqual(currentRef, templateRef)) {
      if (moduleType === 'images' || moduleType === 'files') {
        const preview = buildAssetEntryPreview(templateEntry, 0, moduleType);
        details.push({
          label: moduleType === 'images' ? labels.imageFile : labels.fileAsset,
          value: displayEntryFieldValue(
            getFileEntryDisplayTitle(templateEntry, 0) ?? templateRef,
            labels.emptyValue,
          ),
          imagePreviews: preview ? [preview] : undefined,
          assetPreviewLayout: assetPreviewLayoutForModule(moduleType),
        });
      }
    }
  }

  if (moduleType === 'team_members') {
    const templateAvatar = templateEntry.avatar as
      | Record<string, unknown>
      | undefined;
    const currentAvatar = currentEntry.avatar as
      | Record<string, unknown>
      | undefined;
    const templateRef = templateAvatar?.id ?? templateAvatar?.path;
    const currentRef = currentAvatar?.id ?? currentAvatar?.path;
    if (!entryFieldValuesEqual(currentRef, templateRef)) {
      details.push({
        label: labels.memberAvatar,
        value: displayEntryFieldValue(templateEntry.name, labels.emptyValue),
      });
    }
  }

  return details;
}

function getAssetsModuleOverrideDisplay(
  currentModule: ProfileModuleDto,
  templateModule: ProfileModuleDto,
  labels: TemplateModuleDisplayLabels,
): TemplateFormFieldDisplay | null {
  const moduleType = templateModule.type;
  const templateConfig = (templateModule.config ?? {}) as Record<
    string,
    unknown
  >;
  const currentConfig = (currentModule.config ?? {}) as Record<string, unknown>;
  const templateEntries = getAssetsEntries(templateConfig, moduleType);
  const currentEntries = getAssetsEntries(currentConfig, moduleType);

  const details: NonNullable<TemplateFormFieldDisplay['details']> = [
    ...getAssetsModuleSettingDiffs(
      currentConfig,
      templateConfig,
      moduleType,
      labels,
    ),
  ];

  if (moduleType === 'team_members') {
    if (details.length === 0) {
      return null;
    }
    if (details.length === 1) {
      const only = details[0]!;
      return { single: `${only.label}: ${only.value}` };
    }
    return { details };
  }

  const templateIds = getOrderedEntryIds(templateEntries);
  const currentIds = getOrderedEntryIds(currentEntries);
  const canCompareById =
    templateIds.length === templateEntries.length &&
    currentIds.length === currentEntries.length;

  if (canCompareById && templateIds.join('\0') !== currentIds.join('\0')) {
    if (moduleType === 'images' || moduleType === 'files') {
      const assetPreviews = templateEntries
        .map((entry, index) =>
          buildAssetEntryPreview(
            entry,
            index,
            moduleType as 'images' | 'files',
          ),
        )
        .filter((preview): preview is TemplateAssetPreview => preview !== null);
      details.push({
        label: assetPreviews.length > 0 ? '' : labels.entryOrder,
        value:
          assetPreviews.length > 0
            ? ''
            : templateEntries
                .map((entry, index) =>
                  getAssetEntryLabel(entry, moduleType, index),
                )
                .join(' · '),
        imagePreviews: assetPreviews.length > 0 ? assetPreviews : undefined,
        assetPreviewLayout: assetPreviewLayoutForModule(moduleType),
      });
    } else {
      details.push({
        label: labels.entryOrder,
        value: templateEntries
          .map((entry, index) => getAssetEntryLabel(entry, moduleType, index))
          .join(' · '),
      });
    }
  }

  const currentById = new Map(
    currentEntries
      .filter((entry) => entry.id)
      .map((entry) => [entry.id!, entry]),
  );

  templateEntries.forEach((templateEntry, index) => {
    const entryId = templateEntry.id;
    const currentEntry = entryId
      ? currentById.get(entryId)
      : currentEntries[index];
    if (!currentEntry) {
      return;
    }

    const entryDiffs = getAssetsEntryFieldDiffs(
      currentEntry,
      templateEntry,
      moduleType,
      labels,
    );
    if (entryDiffs.length === 0) {
      return;
    }

    const entryLabel = getAssetEntryLabel(templateEntry, moduleType, index);
    const entryAssetPreview =
      moduleType === 'images' || moduleType === 'files'
        ? buildAssetEntryPreview(
            templateEntry,
            index,
            moduleType as 'images' | 'files',
          )
        : null;
    entryDiffs.forEach((diff, diffIndex) => {
      details.push({
        label: `${entryLabel} – ${diff.label}`,
        value: diff.value,
        imagePreviews:
          entryAssetPreview && diffIndex === 0
            ? [entryAssetPreview]
            : undefined,
        assetPreviewLayout:
          entryAssetPreview && diffIndex === 0
            ? assetPreviewLayoutForModule(moduleType)
            : undefined,
      });
    });
  });

  if (currentEntries.length > templateEntries.length) {
    details.push({
      label: labels.moduleEntries.replace(
        '{count}',
        String(templateEntries.length),
      ),
      value: labels.emptyValue,
    });
  }

  if (details.length === 0) {
    return null;
  }
  if (details.length === 1) {
    const only = details[0]!;
    if (only.imagePreviews?.length) {
      return { details: [only] };
    }
    return { single: `${only.label}: ${only.value}` };
  }
  return { details };
}

/** Template values for only module fields that differ from the template. */
export function getTemplateModuleOverrideDisplay(
  currentModule: ProfileModuleDto,
  templateModule: ProfileModuleDto,
  labels: TemplateModuleDisplayLabels,
): TemplateFormFieldDisplay | null {
  if (!isModuleContentCustomized(currentModule, templateModule)) {
    return null;
  }

  const templateConfig = (templateModule.config ?? {}) as Record<
    string,
    unknown
  >;
  const currentConfig = (currentModule.config ?? {}) as Record<string, unknown>;
  const type = templateModule.type;

  if (isPerEntryListModuleType(type)) {
    return null;
  }

  if (isAssetsListModuleType(type)) {
    return getAssetsModuleOverrideDisplay(
      currentModule,
      templateModule,
      labels,
    );
  }

  const fieldDiffs = getWholeModuleFieldDiffs(
    currentConfig,
    templateConfig,
    type,
    labels,
  );
  if (fieldDiffs.length > 0) {
    if (fieldDiffs.length === 1) {
      const [first] = fieldDiffs;
      return {
        single: `${first!.label}: ${first!.value}`,
      };
    }
    return { details: fieldDiffs };
  }

  if (!templateModuleHasMeaningfulContent(templateModule)) {
    return { single: labels.moduleNoContent };
  }

  return null;
}
